
import { VisaGuideData } from "@/types/visa";

export const visaGuideData: VisaGuideData = {
  "usa": {
    "tourist": {
      overview: "The B-1/B-2 Visitor Visa is for temporary visits for business or pleasure. It generally allows a stay of up to six months.",
      requirements: [
        { title: "Passport", description: "Valid passport with at least six months validity", icon: "passport", isRequired: true },
        { title: "Application Form", description: "DS-160 form completion", icon: "form", isRequired: true },
        { title: "Interview", description: "Attend a visa interview", icon: "interview", isRequired: true },
        { title: "Photos", description: "Passport-sized photographs", icon: "photo", isRequired: true },
        { title: "Fees", description: "Pay the visa application fee", icon: "fee", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a US Visitor Visa",
        timeframe: "Varies",
        cost: "$160",
        steps: [
          { title: "Complete DS-160", description: "Fill out the online application form." },
          { title: "Pay Fee", description: "Pay the non-refundable application fee." },
          { title: "Schedule Interview", description: "Schedule an interview at the US embassy or consulate." },
          { title: "Attend Interview", description: "Attend the visa interview with required documents." }
        ]
      },
      tips: ["Be honest in your application", "Provide all required documents", "Dress professionally for the interview"],
      resources: [
        { name: "US Department of State", url: "https://travel.state.gov/" },
        { name: "US Embassy", url: "https://www.usembassy.gov/" }
      ]
    },
    "student": {
      overview: "The F-1 Student Visa is for individuals wishing to pursue academic studies in the US. It requires enrollment in a SEVP-approved school.",
      requirements: [
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "I-20 Form", description: "Certificate of Eligibility from SEVP-approved school", icon: "form", isRequired: true },
        { title: "SEVIS Fee", description: "Pay the Student and Exchange Visitor Information System fee", icon: "fee", isRequired: true },
        { title: "Application Form", description: "DS-160 form completion", icon: "form", isRequired: true },
        { title: "Interview", description: "Attend a visa interview", icon: "interview", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a US Student Visa",
        timeframe: "Varies",
        cost: "$160",
        steps: [
          { title: "Get Accepted", description: "Receive acceptance from a SEVP-approved school." },
          { title: "Receive I-20", description: "Obtain the Certificate of Eligibility (I-20 form)." },
          { title: "Pay SEVIS Fee", description: "Pay the SEVIS fee before the interview." },
          { title: "Apply for Visa", description: "Apply for the F-1 visa and attend the interview." }
        ]
      },
      tips: ["Prepare for the interview", "Show strong ties to your home country", "Have a clear study plan"],
      resources: [
        { name: "EducationUSA", url: "https://www.educationusa.org/" },
        { name: "SEVP", url: "https://www.ice.gov/sevis" }
      ]
    },
    "work": {
      overview: "The H-1B visa allows US employers to temporarily employ foreign workers in specialty occupations. These occupations generally require a bachelor's degree or higher.",
      requirements: [
        { title: "Job Offer", description: "Job offer from a US employer", icon: "job", isRequired: true },
        { title: "Petition", description: "Employer files a petition with USCIS", icon: "petition", isRequired: true },
        { title: "Application Form", description: "DS-160 form completion", icon: "form", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Interview", description: "Attend a visa interview", icon: "interview", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a US Work Visa",
        timeframe: "Varies",
        cost: "$190",
        steps: [
          { title: "Employer Files Petition", description: "Employer files Form I-129 with USCIS." },
          { title: "Petition Approval", description: "USCIS approves the petition." },
          { title: "Apply for Visa", description: "Apply for the H-1B visa and attend the interview." }
        ]
      },
      tips: ["Ensure your employer is reputable", "Prepare for potential delays", "Understand the prevailing wage requirements"],
      resources: [
        { name: "USCIS", url: "https://www.uscis.gov/" },
        { name: "US Department of Labor", url: "https://www.dol.gov/" }
      ]
    },
    "business": {
      overview: "The E-1/E-2 visa is for citizens of treaty countries who come to the US to engage in substantial trade or investment.",
      requirements: [
        { title: "Nationality", description: "Must be a citizen of a treaty country", icon: "nationality", isRequired: true },
        { title: "Investment", description: "Substantial investment in a US enterprise", icon: "investment", isRequired: true },
        { title: "Business Plan", description: "Detailed business plan", icon: "plan", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "DS-160 form completion", icon: "form", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a US Business Visa",
        timeframe: "Varies",
        cost: "$205",
        steps: [
          { title: "Establish Business", description: "Establish a qualifying business in the US." },
          { title: "Make Investment", description: "Make a substantial investment." },
          { title: "Apply for Visa", description: "Apply for the E-1/E-2 visa and attend the interview." }
        ]
      },
      tips: ["Consult with an immigration attorney", "Document all investments", "Show the business will benefit the US economy"],
      resources: [
        { name: "US Department of State", url: "https://travel.state.gov/" },
        { name: "USCIS", url: "https://www.uscis.gov/" }
      ]
    },
    "family": {
      overview: "The K-1 visa is for the fiancé(e) of a US citizen. It allows the foreign fiancé(e) to enter the US and marry the US citizen within 90 days.",
      requirements: [
        { title: "US Citizen Sponsor", description: "Must be sponsored by a US citizen", icon: "citizen", isRequired: true },
        { title: "Petition", description: "US citizen files a petition with USCIS", icon: "petition", isRequired: true },
        { title: "Intent to Marry", description: "Intent to marry within 90 days", icon: "marriage", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "DS-160 form completion", icon: "form", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a US Family Visa",
        timeframe: "Varies",
        cost: "$265",
        steps: [
          { title: "File Petition", description: "US citizen files Form I-129F with USCIS." },
          { title: "Petition Approval", description: "USCIS approves the petition." },
          { title: "Apply for Visa", description: "Apply for the K-1 visa and attend the interview." }
        ]
      },
      tips: ["Provide evidence of a bona fide relationship", "Prepare for potential delays", "Ensure all documents are translated"],
      resources: [
        { name: "USCIS", url: "https://www.uscis.gov/" },
        { name: "US Department of State", url: "https://travel.state.gov/" }
      ]
    },
    "skilled": {
      overview: "The O-1 visa is for individuals with extraordinary ability or achievement in the sciences, arts, education, business, or athletics.",
      requirements: [
        { title: "Extraordinary Ability", description: "Demonstrate extraordinary ability", icon: "ability", isRequired: true },
        { title: "Petition", description: "Employer files a petition with USCIS", icon: "petition", isRequired: true },
        { title: "Achievements", description: "Evidence of achievements", icon: "achievements", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "DS-160 form completion", icon: "form", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a US Skilled Worker Visa",
        timeframe: "Varies",
        cost: "$190",
        steps: [
          { title: "Employer Files Petition", description: "Employer files Form I-129 with USCIS." },
          { title: "Petition Approval", description: "USCIS approves the petition." },
          { title: "Apply for Visa", description: "Apply for the O-1 visa and attend the interview." }
        ]
      },
      tips: ["Gather extensive evidence of your achievements", "Consult with an immigration attorney", "Highlight your unique skills"],
      resources: [
        { name: "USCIS", url: "https://www.uscis.gov/" },
        { name: "US Department of Labor", url: "https://www.dol.gov/" }
      ]
    },
    "startup": {
      overview: "The E-2 visa, while not specifically for startups, can be used by entrepreneurs from treaty countries who invest in a US business.",
      requirements: [
        { title: "Nationality", description: "Must be a citizen of a treaty country", icon: "nationality", isRequired: true },
        { title: "Investment", description: "Substantial investment in a US enterprise", icon: "investment", isRequired: true },
        { title: "Business Plan", description: "Detailed business plan", icon: "plan", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "DS-160 form completion", icon: "form", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a US Startup Visa",
        timeframe: "Varies",
        cost: "$205",
        steps: [
          { title: "Establish Business", description: "Establish a qualifying business in the US." },
          { title: "Make Investment", description: "Make a substantial investment." },
          { title: "Apply for Visa", description: "Apply for the E-2 visa and attend the interview." }
        ]
      },
      tips: ["Consult with an immigration attorney", "Document all investments", "Show the business will benefit the US economy"],
      resources: [
        { name: "US Department of State", url: "https://travel.state.gov/" },
        { name: "USCIS", url: "https://www.uscis.gov/" }
      ]
    },
    "other": {
      overview: "Various other US visa types exist for specific purposes such as religious workers (R-1), journalists (I), and diplomats (A).",
      requirements: [
        { title: "Varies", description: "Requirements vary depending on the specific visa type", icon: "info", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "DS-160 form completion", icon: "form", isRequired: true },
        { title: "Interview", description: "Attend a visa interview", icon: "interview", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for other US Visa Types",
        timeframe: "Varies",
        cost: "Varies",
        steps: [
          { title: "Determine Eligibility", description: "Determine eligibility for the specific visa type." },
          { title: "Gather Documents", description: "Gather required documents." },
          { title: "Apply for Visa", description: "Apply for the visa and attend the interview." }
        ]
      },
      tips: ["Research the specific visa requirements", "Consult with an immigration attorney", "Provide all required documents"],
      resources: [
        { name: "US Department of State", url: "https://travel.state.gov/" },
        { name: "USCIS", url: "https://www.uscis.gov/" }
      ]
    }
  },
  "uk": {
    "tourist": {
      overview: "The Standard Visitor Visa allows you to visit the UK for tourism, business, study (courses up to 6 months), or other permitted activities.",
      requirements: [
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "Online application form", icon: "form", isRequired: true },
        { title: "Proof of Funds", description: "Evidence of sufficient funds", icon: "funds", isRequired: true },
        { title: "Travel History", description: "Details of previous travel", icon: "travel", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a UK Visitor Visa",
        timeframe: "3-4 weeks",
        cost: "£100",
        steps: [
          { title: "Apply Online", description: "Complete the online application form." },
          { title: "Pay Fee", description: "Pay the application fee." },
          { title: "Submit Documents", description: "Submit supporting documents." },
          { title: "Attend Interview", description: "Attend an interview if required." }
        ]
      },
      tips: ["Provide accurate information", "Show strong ties to your home country", "Book flights and accommodation in advance"],
      resources: [
        { name: "UK Visas and Immigration", url: "https://www.gov.uk/browse/visas-immigration" },
        { name: "UK Embassy", url: "https://www.gov.uk/world/organisations" }
      ]
    },
    "student": {
      overview: "The Student Visa (formerly Tier 4) is for individuals who want to study in the UK. It requires sponsorship from a licensed student sponsor.",
      requirements: [
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "CAS", description: "Confirmation of Acceptance for Studies from a licensed sponsor", icon: "form", isRequired: true },
        { title: "Proof of Funds", description: "Evidence of sufficient funds", icon: "funds", isRequired: true },
        { title: "English Proficiency", description: "Proof of English language proficiency", icon: "language", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a UK Student Visa",
        timeframe: "3-4 weeks",
        cost: "£348",
        steps: [
          { title: "Get Accepted", description: "Receive an offer from a licensed student sponsor." },
          { title: "Receive CAS", description: "Obtain the Confirmation of Acceptance for Studies." },
          { title: "Apply Online", description: "Apply for the Student Visa online." },
          { title: "Attend Interview", description: "Attend an interview if required." }
        ]
      },
      tips: ["Prepare for the interview", "Show a genuine intention to study", "Meet the financial requirements"],
      resources: [
        { name: "UK Visas and Immigration", url: "https://www.gov.uk/student-visa" },
        { name: "UCAS", url: "https://www.ucas.com/" }
      ]
    },
    "work": {
      overview: "The Skilled Worker Visa allows employers to sponsor foreign workers to fill specific job roles in the UK.",
      requirements: [
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Job Offer", description: "Job offer from a licensed sponsor", icon: "job", isRequired: true },
        { title: "Certificate of Sponsorship", description: "Certificate of Sponsorship from the employer", icon: "form", isRequired: true },
        { title: "English Proficiency", description: "Proof of English language proficiency", icon: "language", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a UK Work Visa",
        timeframe: "3-4 weeks",
        cost: "£610",
        steps: [
          { title: "Get Sponsored", description: "Receive a job offer from a licensed sponsor." },
          { title: "Receive CoS", description: "Obtain the Certificate of Sponsorship." },
          { title: "Apply Online", description: "Apply for the Skilled Worker Visa online." },
          { title: "Attend Interview", description: "Attend an interview if required." }
        ]
      },
      tips: ["Ensure your employer is a licensed sponsor", "Meet the skill level requirements", "Understand the salary thresholds"],
      resources: [
        { name: "UK Visas and Immigration", url: "https://www.gov.uk/skilled-worker-visa" },
        { name: "Home Office", url: "https://www.gov.uk/government/organisations/home-office" }
      ]
    },
    "business": {
      overview: "The Innovator Visa is for experienced businesspersons seeking to establish a new business in the UK.",
      requirements: [
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Endorsement", description: "Endorsement from an approved body", icon: "endorsement", isRequired: true },
        { title: "Business Plan", description: "Detailed business plan", icon: "plan", isRequired: true },
        { title: "Investment Funds", description: "£50,000 investment funds", icon: "funds", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a UK Business Visa",
        timeframe: "3-4 weeks",
        cost: "£1,021",
        steps: [
          { title: "Get Endorsed", description: "Obtain endorsement from an approved body." },
          { title: "Prepare Business Plan", description: "Prepare a detailed business plan." },
          { title: "Apply Online", description: "Apply for the Innovator Visa online." },
          { title: "Attend Interview", description: "Attend an interview if required." }
        ]
      },
      tips: ["Develop a unique and innovative business idea", "Secure endorsement from a reputable body", "Meet the investment requirements"],
      resources: [
        { name: "UK Visas and Immigration", url: "https://www.gov.uk/innovator-visa" },
        { name: "Home Office", url: "https://www.gov.uk/government/organisations/home-office" }
      ]
    },
    "family": {
      overview: "The Family Visa allows individuals to join family members who are British citizens or settled in the UK.",
      requirements: [
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Sponsor", description: "Sponsor who is a British citizen or settled person", icon: "sponsor", isRequired: true },
        { title: "Relationship Proof", description: "Evidence of a genuine relationship", icon: "relationship", isRequired: true },
        { title: "Financial Stability", description: "Meet the financial requirements", icon: "funds", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a UK Family Visa",
        timeframe: "8-12 weeks",
        cost: "£1,523",
        steps: [
          { title: "Meet Requirements", description: "Ensure you meet all eligibility requirements." },
          { title: "Apply Online", description: "Apply for the Family Visa online." },
          { title: "Submit Documents", description: "Submit supporting documents." },
          { title: "Attend Interview", description: "Attend an interview if required." }
        ]
      },
      tips: ["Provide comprehensive evidence of your relationship", "Meet the financial requirements", "Ensure your sponsor meets the eligibility criteria"],
      resources: [
        { name: "UK Visas and Immigration", url: "https://www.gov.uk/uk-family-visa" },
        { name: "Home Office", url: "https://www.gov.uk/government/organisations/home-office" }
      ]
    },
    "skilled": {
      overview: "The Global Talent Visa is for leaders or potential leaders in academia, arts, culture, and digital technology.",
      requirements: [
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Endorsement", description: "Endorsement from an approved body", icon: "endorsement", isRequired: true },
        { title: "Achievements", description: "Evidence of exceptional talent", icon: "achievements", isRequired: true },
        { title: "Field Expertise", description: "Expertise in a qualifying field", icon: "expertise", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a UK Skilled Worker Visa",
        timeframe: "3-4 weeks",
        cost: "£608",
        steps: [
          { title: "Get Endorsed", description: "Obtain endorsement from an approved body." },
          { title: "Apply Online", description: "Apply for the Global Talent Visa online." },
          { title: "Submit Documents", description: "Submit supporting documents." },
          { title: "Attend Interview", description: "Attend an interview if required." }
        ]
      },
      tips: ["Show exceptional talent in your field", "Secure endorsement from a reputable body", "Provide strong evidence of your achievements"],
      resources: [
        { name: "UK Visas and Immigration", url: "https://www.gov.uk/global-talent-visa" },
        { name: "Home Office", url: "https://www.gov.uk/government/organisations/home-office" }
      ]
    },
    "startup": {
      overview: "The Start-up Visa is for individuals starting an innovative business in the UK for the first time.",
      requirements: [
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Endorsement", description: "Endorsement from an approved body", icon: "endorsement", isRequired: true },
        { title: "Business Plan", description: "Detailed business plan", icon: "plan", isRequired: true },
        { title: "Innovative Idea", description: "Innovative and viable business idea", icon: "idea", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a UK Startup Visa",
        timeframe: "3-4 weeks",
        cost: "£363",
        steps: [
          { title: "Get Endorsed", description: "Obtain endorsement from an approved body." },
          { title: "Prepare Business Plan", description: "Prepare a detailed business plan." },
          { title: "Apply Online", description: "Apply for the Start-up Visa online." },
          { title: "Attend Interview", description: "Attend an interview if required." }
        ]
      },
      tips: ["Develop an innovative and scalable business idea", "Secure endorsement from a reputable body", "Demonstrate your commitment to the UK"],
      resources: [
        { name: "UK Visas and Immigration", url: "https://www.gov.uk/start-up-visa" },
        { name: "Home Office", url: "https://www.gov.uk/government/organisations/home-office" }
      ]
    },
    "other": {
      overview: "Various other UK visa types exist for specific purposes such as religious workers, domestic workers, and representatives of overseas businesses.",
      requirements: [
        { title: "Varies", description: "Requirements vary depending on the specific visa type", icon: "info", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "Online application form", icon: "form", isRequired: true },
        { title: "Supporting Documents", description: "Supporting documents as required", icon: "documents", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for other UK Visa Types",
        timeframe: "Varies",
        cost: "Varies",
        steps: [
          { title: "Determine Eligibility", description: "Determine eligibility for the specific visa type." },
          { title: "Gather Documents", description: "Gather required documents." },
          { title: "Apply Online", description: "Apply for the visa online." },
          { title: "Attend Interview", description: "Attend an interview if required." }
        ]
      },
      tips: ["Research the specific visa requirements", "Provide accurate information", "Submit all required documents"],
      resources: [
        { name: "UK Visas and Immigration", url: "https://www.gov.uk/browse/visas-immigration" },
        { name: "Home Office", url: "https://www.gov.uk/government/organisations/home-office" }
      ]
    }
  },
  "canada": {
    "tourist": {
      overview: "A Temporary Resident Visa (TRV) or Visitor Visa allows you to visit Canada for tourism, visiting family/friends, or business meetings. The visa can be single-entry or multiple-entry, valid for up to 10 years or until your passport expires.",
      requirements: [
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "Completed application form", icon: "form", isRequired: true },
        { title: "Proof of Funds", description: "Sufficient funds to cover your stay", icon: "funds", isRequired: true },
        { title: "Travel History", description: "Details of your travel history", icon: "travel", isRequired: false },
        { title: "Letter of Invitation", description: "If visiting family/friends", icon: "invitation", isRequired: false }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a Canadian Visitor Visa",
        timeframe: "Varies",
        cost: "$100 CAD",
        steps: [
          { title: "Apply Online", description: "Create an account and apply online through the IRCC website." },
          { title: "Submit Documents", description: "Upload required documents." },
          { title: "Pay Fee", description: "Pay the application fee." },
          { title: "Biometrics", description: "Provide biometrics if required." }
        ]
      },
      tips: [
        "Apply well in advance of your intended travel date",
        "Provide accurate and truthful information",
        "Ensure your passport is valid for the duration of your stay"
      ],
      resources: [
        { name: "Immigration, Refugees and Citizenship Canada (IRCC)", url: "https://www.canada.ca/en/immigration-refugees-citizenship.html" },
        { name: "Canadian Embassy", url: "https://www.canadainternational.gc.ca/world-monde/index.aspx" }
      ],
      originSpecific: {
        "usa": {
          eligibility: "US citizens do not need a visa to visit Canada. You only need a valid passport and may need an Electronic Travel Authorization (eTA) if arriving by air.",
          processingTime: "eTA processing usually takes minutes, but can take up to 72 hours.",
          specialNotes: [
            "eTA is valid for 5 years or until passport expires",
            "Must have a valid US passport",
            "No visa required for stays up to 6 months"
          ]
        },
        "uk": {
          eligibility: "UK citizens do not need a visa to visit Canada. However, you must obtain an Electronic Travel Authorization (eTA) before flying to Canada.",
          processingTime: "eTA processing usually takes minutes, but can take up to 72 hours.",
          specialNotes: [
            "eTA is valid for 5 years or until passport expires",
            "Must have a valid UK passport",
            "No visa required for stays up to 6 months"
          ]
        },
        "india": {
          eligibility: "Indian citizens need a valid Temporary Resident Visa (TRV) to visit Canada.",
          processingTime: "20-30 business days",
          specialNotes: [
            "Biometrics required",
            "Must show proof of funds",
            "May need medical exam"
          ]
        },
        "china": {
          eligibility: "Chinese citizens need a valid Temporary Resident Visa (TRV) to visit Canada.",
          processingTime: "30-60 business days",
          specialNotes: [
            "Biometrics required",
            "Must show proof of funds",
            "May need medical exam"
          ]
        }
      }
    },
    "student": {
      overview: "A Canadian Study Permit allows international students to study at designated learning institutions (DLIs) in Canada. This permit is typically valid for the duration of your study program plus an additional 90 days. Most Study Permit holders can also work part-time during studies and full-time during scheduled breaks.",
      requirements: [
        { title: "Acceptance Letter", description: "Acceptance letter from a designated learning institution (DLI)", icon: "letter", isRequired: true },
        { title: "Proof of Funds", description: "Sufficient funds to cover tuition, living expenses, and return transportation", icon: "funds", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "Completed application form", icon: "form", isRequired: true },
        { title: "Study Plan", description: "A clear study plan", icon: "plan", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a Canadian Study Permit",
        timeframe: "Varies",
        cost: "$150 CAD",
        steps: [
          { title: "Apply Online", description: "Apply online through the IRCC website." },
          { title: "Submit Documents", description: "Upload required documents." },
          { title: "Pay Fee", description: "Pay the application fee." },
          { title: "Biometrics", description: "Provide biometrics if required." }
        ]
      },
      tips: [
        "Apply well in advance of your program start date",
        "Provide a clear and detailed study plan",
        "Ensure you meet the financial requirements"
      ],
      resources: [
        { name: "Immigration, Refugees and Citizenship Canada (IRCC)", url: "https://www.canada.ca/en/immigration-refugees-citizenship.html" },
        { name: "Canadian Embassy", url: "https://www.canadainternational.gc.ca/world-monde/index.aspx" }
      ],
      originSpecific: {
        "usa": {
          eligibility: "US students need a Study Permit for programs longer than 6 months. A TRV is not required.",
          processingTime: "3-8 weeks",
          specialNotes: [
            "Can work up to 20 hours per week during studies",
            "Full-time work allowed during scheduled breaks",
            "May be eligible for Post-Graduation Work Permit"
          ]
        },
        "uk": {
          eligibility: "UK students need a Study Permit for programs longer than 6 months. A TRV is not required.",
          processingTime: "3-8 weeks",
          specialNotes: [
            "Can work up to 20 hours per week during studies",
            "Full-time work allowed during scheduled breaks",
            "May be eligible for Post-Graduation Work Permit"
          ]
        },
        "india": {
          eligibility: "Indian students need both a Study Permit and TRV. Must be accepted by a DLI.",
          processingTime: "8-12 weeks",
          specialNotes: [
            "Biometrics required",
            "Must show proof of funds for tuition and living expenses",
            "Medical exam may be required"
          ]
        }
      }
    },
    "work": {
      overview: "A Canadian Work Permit allows foreign nationals to work in Canada temporarily. There are two main types: Open Work Permits (allowing work for any employer) and Employer-Specific Work Permits (tied to a specific employer). Most work permits require a Labour Market Impact Assessment (LMIA) unless exempt under specific programs.",
      requirements: [
        { title: "Job Offer", description: "Valid job offer from a Canadian employer", icon: "job", isRequired: true },
        { title: "LMIA", description: "Labour Market Impact Assessment (if required)", icon: "lmia", isRequired: false },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "Completed application form", icon: "form", isRequired: true },
        { title: "Proof of Qualifications", description: "Evidence of qualifications for the job", icon: "qualifications", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a Canadian Work Permit",
        timeframe: "Varies",
        cost: "$155 CAD",
        steps: [
          { title: "Obtain Job Offer", description: "Secure a job offer from a Canadian employer." },
          { title: "LMIA Process", description: "Employer obtains LMIA if required." },
          { title: "Apply Online", description: "Apply for work permit online." },
          { title: "Biometrics", description: "Provide biometrics if required." }
        ]
      },
      tips: [
        "Ensure your employer is legitimate and authorized to hire foreign workers",
        "Apply well in advance of your intended start date",
        "Be aware of provincial nominee programs that may be applicable"
      ],
      resources: [
        { name: "Immigration, Refugees and Citizenship Canada (IRCC)", url: "https://www.canada.ca/en/immigration-refugees-citizenship.html" },
        { name: "Canadian Embassy", url: "https://www.canadainternational.gc.ca/world-monde/index.aspx" }
      ],
      originSpecific: {
        "usa": {
          eligibility: "US citizens can apply for work permits under various programs including NAFTA/CUSMA professionals.",
          processingTime: "4-8 weeks",
          specialNotes: [
            "May qualify for work permit exemptions under certain categories",
            "Some professions qualify for simplified process under CUSMA",
            "Spouses may be eligible for open work permits"
          ]
        },
        "uk": {
          eligibility: "UK citizens need a work permit for most employment in Canada. May qualify for International Experience Canada (IEC) program.",
          processingTime: "8-12 weeks",
          specialNotes: [
            "Young professionals (18-30) can apply through IEC",
            "Federal Skilled Worker program available for permanent residency",
            "Express Entry system can fast-track qualified workers"
          ]
        },
        "india": {
          eligibility: "Indian citizens need both a work permit and TRV to work in Canada.",
          processingTime: "8-16 weeks",
          specialNotes: [
            "Biometrics required",
            "Medical exam may be required",
            "Priority processing for certain in-demand occupations"
          ]
        }
      }
    },
    "business": {
      overview: "Canada offers several business immigration pathways for entrepreneurs, investors, and self-employed individuals. These programs allow foreign nationals to establish businesses in Canada and eventually apply for permanent residence.",
      requirements: [
        { title: "Business Proposal", description: "Detailed business plan", icon: "plan", isRequired: true },
        { title: "Net Worth", description: "Proof of net worth (varies by program)", icon: "funds", isRequired: true },
        { title: "Experience", description: "Business ownership or senior management experience", icon: "experience", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Investment Funds", description: "Funds to invest in a Canadian business", icon: "investment", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for a Canadian Business Visa",
        timeframe: "12-16 months",
        cost: "Varies by program",
        steps: [
          { title: "Select Program", description: "Choose the appropriate business immigration program." },
          { title: "Business Plan", description: "Develop a comprehensive business plan." },
          { title: "Apply", description: "Submit application with supporting documents." },
          { title: "Interview", description: "Attend an interview if required." }
        ]
      },
      tips: [
        "Research provincial nominee programs with entrepreneur streams",
        "Consult with an immigration lawyer or consultant",
        "Visit Canada on an exploratory trip before applying"
      ],
      resources: [
        { name: "Immigration, Refugees and Citizenship Canada (IRCC)", url: "https://www.canada.ca/en/immigration-refugees-citizenship.html" },
        { name: "Start-up Visa Program", url: "https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/start-visa.html" }
      ]
    },
    "family": {
      overview: "The Family Class Sponsorship program allows Canadian citizens and permanent residents to sponsor eligible family members for permanent residence in Canada.",
      requirements: [
        { title: "Sponsor", description: "Canadian citizen or permanent resident sponsor", icon: "citizen", isRequired: true },
        { title: "Relationship", description: "Proof of eligible relationship", icon: "relationship", isRequired: true },
        { title: "Financial Support", description: "Proof that the sponsor can support the applicant", icon: "funds", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "Completed application forms", icon: "form", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for Canadian Family Sponsorship",
        timeframe: "12-24 months",
        cost: "$1,050+ CAD",
        steps: [
          { title: "Sponsor Application", description: "Sponsor submits sponsorship application." },
          { title: "Principal Application", description: "Family member submits permanent residence application." },
          { title: "Biometrics", description: "Provide biometrics if required." },
          { title: "Medical Exam", description: "Complete medical examination." }
        ]
      },
      tips: [
        "Ensure all documents are translated to English or French",
        "Provide comprehensive proof of your relationship",
        "Be aware of processing times for different family member categories"
      ],
      resources: [
        { name: "Immigration, Refugees and Citizenship Canada (IRCC)", url: "https://www.canada.ca/en/immigration-refugees-citizenship.html" },
        { name: "Family Sponsorship Guide", url: "https://www.canada.ca/en/immigration-refugees-citizenship/services/application/application-forms-guides/guide-5525-basic-guide-sponsor-your-spouse-partner-child.html" }
      ]
    },
    "skilled": {
      overview: "Canada's Express Entry system manages applications for permanent residence from skilled workers. It includes the Federal Skilled Worker Program, Federal Skilled Trades Program, and Canadian Experience Class.",
      requirements: [
        { title: "Education", description: "Post-secondary education credentials", icon: "education", isRequired: true },
        { title: "Work Experience", description: "Skilled work experience", icon: "experience", isRequired: true },
        { title: "Language Proficiency", description: "English/French language skills (CLB/NCLC scores)", icon: "language", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Funds", description: "Proof of settlement funds (unless have Canadian job offer)", icon: "funds", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for Canadian Express Entry",
        timeframe: "6-12 months",
        cost: "$2,300+ CAD",
        steps: [
          { title: "Create Profile", description: "Create an Express Entry profile." },
          { title: "Receive ITA", description: "Receive an Invitation to Apply if selected." },
          { title: "Submit Application", description: "Submit complete PR application within 60 days." },
          { title: "Medical & Background", description: "Complete medical exam and background checks." }
        ]
      },
      tips: [
        "Get your education credentials assessed (ECA)",
        "Take official language tests (IELTS, CELPIP, TEF, TCF)",
        "Improve your CRS score with provincial nominations or job offers"
      ],
      resources: [
        { name: "Immigration, Refugees and Citizenship Canada (IRCC)", url: "https://www.canada.ca/en/immigration-refugees-citizenship.html" },
        { name: "Express Entry", url: "https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/express-entry.html" }
      ]
    },
    "startup": {
      overview: "The Start-up Visa Program targets innovative entrepreneurs who can create jobs for Canadians and compete globally. Successful applicants receive permanent residence to establish their business in Canada.",
      requirements: [
        { title: "Business Idea", description: "Innovative business concept", icon: "idea", isRequired: true },
        { title: "Support Letter", description: "Commitment from designated organization", icon: "letter", isRequired: true },
        { title: "Language Proficiency", description: "English/French language skills (CLB/NCLC 5+)", icon: "language", isRequired: true },
        { title: "Funds", description: "Proof of settlement funds", icon: "funds", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for Canadian Start-up Visa",
        timeframe: "12-16 months",
        cost: "$2,075+ CAD",
        steps: [
          { title: "Get Support", description: "Secure commitment from designated organization." },
          { title: "Submit Application", description: "Submit application with supporting documents." },
          { title: "Biometrics", description: "Provide biometrics if required." },
          { title: "Medical Exam", description: "Complete medical examination." }
        ]
      },
      tips: [
        "Research designated venture capital funds, angel investor groups, or business incubators",
        "Develop a solid business plan showing innovation and growth potential",
        "Consider attending Canadian startup events to network with potential supporters"
      ],
      resources: [
        { name: "Immigration, Refugees and Citizenship Canada (IRCC)", url: "https://www.canada.ca/en/immigration-refugees-citizenship.html" },
        { name: "Start-up Visa Program", url: "https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/start-visa.html" }
      ]
    },
    "other": {
      overview: "Canada offers various specialized immigration programs including the Self-Employed Persons Program, Caregivers Program, Rural and Northern Immigration Pilot, and Atlantic Immigration Pilot.",
      requirements: [
        { title: "Varies", description: "Requirements vary by program", icon: "info", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "Completed application forms", icon: "form", isRequired: true },
        { title: "Supporting Documents", description: "Program-specific documentation", icon: "documents", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for other Canadian visa types",
        timeframe: "Varies by program",
        cost: "Varies by program",
        steps: [
          { title: "Select Program", description: "Identify the appropriate immigration program." },
          { title: "Gather Documents", description: "Collect required documentation." },
          { title: "Submit Application", description: "Submit application with supporting documents." },
          { title: "Follow Up", description: "Respond to requests for additional information." }
        ]
      },
      tips: [
        "Research all available immigration pathways",
        "Consider provincial nominee programs for specific skills",
        "Consult with an immigration professional for complex cases"
      ],
      resources: [
        { name: "Immigration, Refugees and Citizenship Canada (IRCC)", url: "https://www.canada.ca/en/immigration-refugees-citizenship.html" },
        { name: "Provincial Nominee Programs", url: "https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/provincial-nominees.html" }
      ]
    }
  },
  "australia": {
    "tourist": {
      overview: "The Australian Visitor visa (subclass 600) allows you to visit Australia for tourism or business visitor activities.",
      requirements: [
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "Completed application form", icon: "form", isRequired: true },
        { title: "Proof of Funds", description: "Sufficient funds for your stay", icon: "funds", isRequired: true },
        { title: "Travel History", description: "Details of your travel history", icon: "travel", isRequired: false }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for an Australian Visitor Visa",
        timeframe: "1-4 weeks",
        cost: "AUD 145",
        steps: [
          { title: "Apply Online", description: "Complete the online application form." },
          { title: "Pay Fee", description: "Pay the application fee." },
          { title: "Submit Documents", description: "Upload supporting documents." },
          { title: "Biometrics", description: "Provide biometrics if required." }
        ]
      },
      tips: ["Apply well in advance", "Provide accurate information", "Ensure your passport has at least 6 months validity"],
      resources: [
        { name: "Department of Home Affairs", url: "https://immi.homeaffairs.gov.au/" },
        { name: "Australian Embassy", url: "https://www.dfat.gov.au/about-us/our-locations/missions" }
      ]
    },
    "student": {
      overview: "The Student visa (subclass 500) allows you to study in Australia at a registered institution.",
      requirements: [
        { title: "CoE", description: "Confirmation of Enrollment from an Australian institution", icon: "form", isRequired: true },
        { title: "Proof of Funds", description: "Evidence of sufficient funds", icon: "funds", isRequired: true },
        { title: "Health Insurance", description: "Overseas Student Health Cover (OSHC)", icon: "health", isRequired: true },
        { title: "English Proficiency", description: "Proof of English language proficiency", icon: "language", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for an Australian Student Visa",
        timeframe: "4-6 weeks",
        cost: "AUD 620",
        steps: [
          { title: "Get Accepted", description: "Receive acceptance from an Australian institution." },
          { title: "Apply Online", description: "Apply for the Student Visa online." },
          { title: "Health Exam", description: "Complete health examination if required." },
          { title: "Biometrics", description: "Provide biometrics if required." }
        ]
      },
      tips: ["Apply at least 6-8 weeks before your course starts", "Ensure your GTE (Genuine Temporary Entrant) statement is strong", "Arrange health insurance before applying"],
      resources: [
        { name: "Department of Home Affairs", url: "https://immi.homeaffairs.gov.au/" },
        { name: "Study Australia", url: "https://www.studyinaustralia.gov.au/" }
      ]
    },
    "work": {
      overview: "The Temporary Skill Shortage visa (subclass 482) allows employers to sponsor skilled workers when they cannot find an Australian to fill the position.",
      requirements: [
        { title: "Job Offer", description: "Job offer from an approved Australian employer", icon: "job", isRequired: true },
        { title: "Nomination", description: "Nomination by an approved sponsor", icon: "nomination", isRequired: true },
        { title: "Skills Assessment", description: "Skills assessment for your occupation", icon: "skills", isRequired: true },
        { title: "English Proficiency", description: "Proof of English language proficiency", icon: "language", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for an Australian Work Visa",
        timeframe: "2-4 months",
        cost: "AUD 2,645+",
        steps: [
          { title: "Employer Sponsorship", description: "Employer becomes an approved sponsor." },
          { title: "Nomination", description: "Employer nominates you for a position." },
          { title: "Apply for Visa", description: "Apply for the work visa." },
          { title: "Health Checks", description: "Complete health examination and police checks." }
        ]
      },
      tips: ["Ensure your skills match the nominated occupation", "Check if your occupation is on the relevant skilled occupation list", "Be aware of English language requirements"],
      resources: [
        { name: "Department of Home Affairs", url: "https://immi.homeaffairs.gov.au/" },
        { name: "Skilled Occupation Lists", url: "https://immi.homeaffairs.gov.au/visas/working-in-australia/skill-occupation-list" }
      ]
    },
    "business": {
      overview: "The Business Innovation and Investment visa (subclass 188) is for entrepreneurs, investors, and business owners who want to conduct business in Australia.",
      requirements: [
        { title: "Business History", description: "Successful business career", icon: "business", isRequired: true },
        { title: "Net Assets", description: "Net business and personal assets", icon: "assets", isRequired: true },
        { title: "Business Plan", description: "Detailed business plan for Australian business", icon: "plan", isRequired: true },
        { title: "EOI", description: "Expression of Interest through SkillSelect", icon: "eoi", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for an Australian Business Visa",
        timeframe: "12-18 months",
        cost: "AUD 5,290+",
        steps: [
          { title: "Submit EOI", description: "Submit Expression of Interest through SkillSelect." },
          { title: "Invitation", description: "Receive invitation to apply." },
          { title: "Apply for Visa", description: "Apply for the appropriate business visa." },
          { title: "State Nomination", description: "Obtain state or territory nomination if required." }
        ]
      },
      tips: ["Research different business visa streams", "Consider state-specific requirements", "Consult with a migration agent specializing in business visas"],
      resources: [
        { name: "Department of Home Affairs", url: "https://immi.homeaffairs.gov.au/" },
        { name: "Business Innovation and Investment Program", url: "https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/business-innovation-and-investment-188" }
      ]
    },
    "family": {
      overview: "Family visas allow Australian citizens, permanent residents, or eligible New Zealand citizens to sponsor eligible relatives to come to Australia.",
      requirements: [
        { title: "Sponsor", description: "Eligible sponsor", icon: "sponsor", isRequired: true },
        { title: "Relationship Proof", description: "Evidence of genuine relationship", icon: "relationship", isRequired: true },
        { title: "Health Requirements", description: "Meet health requirements", icon: "health", isRequired: true },
        { title: "Character Requirements", description: "Meet character requirements", icon: "character", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for an Australian Family Visa",
        timeframe: "12-30 months",
        cost: "AUD 1,290-7,850",
        steps: [
          { title: "Choose Visa", description: "Determine the appropriate family visa." },
          { title: "Sponsor Application", description: "Sponsor submits sponsorship application." },
          { title: "Main Application", description: "Applicant submits visa application." },
          { title: "Assessment", description: "Application assessment and additional information requests." }
        ]
      },
      tips: ["Be patient as processing times can be lengthy", "Provide comprehensive evidence of your relationship", "Consider applying for visitor visa while waiting for family visa"],
      resources: [
        { name: "Department of Home Affairs", url: "https://immi.homeaffairs.gov.au/" },
        { name: "Family Visa Information", url: "https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-finder/join-family" }
      ]
    },
    "skilled": {
      overview: "The Skilled Migration Program allows skilled workers to migrate to Australia permanently. The main pathways include the Skilled Independent visa (subclass 189), Skilled Nominated visa (subclass 190), and Skilled Work Regional visa (subclass 491).",
      requirements: [
        { title: "Skills Assessment", description: "Positive skills assessment for your occupation", icon: "skills", isRequired: true },
        { title: "Points Test", description: "Pass the points test (minimum 65 points)", icon: "points", isRequired: true },
        { title: "Age", description: "Be under 45 years of age", icon: "age", isRequired: true },
        { title: "English Proficiency", description: "Competent English or higher", icon: "language", isRequired: true },
        { title: "EOI", description: "Expression of Interest through SkillSelect", icon: "eoi", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for Australian Skilled Migration",
        timeframe: "6-12 months",
        cost: "AUD 4,045",
        steps: [
          { title: "Skills Assessment", description: "Obtain a positive skills assessment." },
          { title: "Submit EOI", description: "Submit Expression of Interest through SkillSelect." },
          { title: "Receive Invitation", description: "Receive invitation to apply for visa." },
          { title: "Apply for Visa", description: "Submit complete visa application." }
        ]
      },
      tips: ["Research occupations in demand", "Improve your points score through language tests, additional qualifications, or work experience", "Consider state nomination to increase your chances"],
      resources: [
        { name: "Department of Home Affairs", url: "https://immi.homeaffairs.gov.au/" },
        { name: "SkillSelect", url: "https://immi.homeaffairs.gov.au/visas/working-in-australia/skillselect" }
      ]
    },
    "startup": {
      overview: "The Global Talent visa program is designed for highly skilled professionals in target sectors to work and live permanently in Australia.",
      requirements: [
        { title: "International Recognition", description: "International recognition in target sector", icon: "recognition", isRequired: true },
        { title: "Nomination", description: "Nomination by an Australian citizen, PR, eligible NZ citizen, or organization", icon: "nomination", isRequired: false },
        { title: "Salary", description: "Ability to attract salary at or above the Fair Work high income threshold", icon: "salary", isRequired: true },
        { title: "Expression of Interest", description: "Submit Global Talent EOI", icon: "eoi", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for Australian Global Talent Visa",
        timeframe: "6-9 months",
        cost: "AUD 4,110",
        steps: [
          { title: "Submit EOI", description: "Submit Expression of Interest for Global Talent." },
          { title: "Receive UID", description: "Receive unique identifier if selected." },
          { title: "Apply for Visa", description: "Apply for the Global Talent visa." },
          { title: "Assessment", description: "Application assessment and additional information requests." }
        ]
      },
      tips: ["Highlight achievements in your field", "Demonstrate how you will contribute to Australia", "Include evidence of professional recognition"],
      resources: [
        { name: "Department of Home Affairs", url: "https://immi.homeaffairs.gov.au/" },
        { name: "Global Talent Program", url: "https://immi.homeaffairs.gov.au/visas/working-in-australia/visas-for-innovation/global-talent-independent-program" }
      ]
    },
    "other": {
      overview: "Australia offers various other visa types for specific purposes such as working holidays, retirement, religious work, and more.",
      requirements: [
        { title: "Varies", description: "Requirements vary by visa type", icon: "info", isRequired: true },
        { title: "Passport", description: "Valid passport", icon: "passport", isRequired: true },
        { title: "Application Form", description: "Completed application form", icon: "form", isRequired: true },
        { title: "Supporting Documents", description: "Visa-specific documentation", icon: "documents", isRequired: true }
      ],
      process: {
        title: "Application Process",
        description: "Steps to apply for other Australian visa types",
        timeframe: "Varies by visa type",
        cost: "Varies by visa type",
        steps: [
          { title: "Research Options", description: "Identify the appropriate visa type." },
          { title: "Check Eligibility", description: "Ensure you meet the eligibility criteria." },
          { title: "Apply for Visa", description: "Submit visa application with supporting documents." },
          { title: "Assessment", description: "Application assessment and processing." }
        ]
      },
      tips: ["Check the Department of Home Affairs website for detailed information", "Be aware of processing times", "Ensure you meet specific visa requirements"],
      resources: [
        { name: "Department of Home Affairs", url: "https://immi.homeaffairs.gov.au/" },
        { name: "Visa Finder", url: "https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-finder" }
      ]
    }
  }
};
